import { Button } from "@/components/ui/UIElements/Button";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../";
import Pagination from "../Data/Pagination";
import { apiClient, buildQueryParams } from "@finitless/shared/lib";

const BusinessesTab = ({
  searchInput,
  setSearchInput,
  handleClearSearch,
  handleSelectAll,
  handleSelectNone,
  handleSelectAllBusinesses, // Optional: if provided, use it; otherwise use internal implementation
  handleBusinessToggle,
  selectedBusinessIds,
  setSelectedBusinessIds, // Required for internal handleSelectAllBusinesses
  isLoadingBusinesses,
  assignBusinessMutation,
  businesses,
  currentPage,
  pagination,
  handlePageChange,
  searchTerm,
  onUpdateBusinesses, // Callback function to update businesses (receives array of business IDs)
  initialSelectedBusinessIds, // For error reversion
}) => {
  const { t } = useTranslation("brands");
  const [isLoadingAllBusinesses, setIsLoadingAllBusinesses] = useState(false);
  const searchInputRef = useRef(null);

  const clearSearchAndRestoreFocus = () => {
    handleClearSearch();
    searchInputRef.current?.focus();
  };

  // Helper to build where clause for business filtering
  const buildBusinessWhereClause = (enabledFilter, searchTerm) => {
    const conditions = [];

    // Add enabled filter condition
    if (enabledFilter === "enabled" || enabledFilter === "disabled") {
      conditions.push({
        attribute: "enabled",
        value: enabledFilter === "enabled",
      });
    }

    // Add search condition for business name
    if (searchTerm && searchTerm.trim() !== "") {
      const _conditions = [
        {
          attribute: "name",
          value: {
            condition: "ilike",
            value: `%${searchTerm.trim()}%`,
          },
        },
      ];
      conditions.push({
        conector: "OR",
        conditions: _conditions,
      });
    }

    return {
      conditions,
      conector: "AND",
    };
  };

  // Internal implementation of handleSelectAllBusinesses
  const handleSelectAllBusinessesInternal = async () => {
    if (!onUpdateBusinesses || !setSelectedBusinessIds) {
      console.warn(
        "handleSelectAllBusinesses: onUpdateBusinesses and setSelectedBusinessIds are required",
      );
      return;
    }

    setIsLoadingAllBusinesses(true);
    const previousSelectedIds = new Set(selectedBusinessIds);

    try {
      // Build where clause with current search term
      const whereClause = buildBusinessWhereClause("all", searchTerm);

      const queryParams = {
        params: "id,name",
        mode: "dashboard",
        where: whereClause,
      };

      const queryString = buildQueryParams(queryParams);
      const response = await apiClient.get(
        `/business${queryString ? `?${queryString}` : ""}`,
      );

      const allBusinesses = response?.result || [];
      const allBusinessIds = new Set(allBusinesses.map((b) => b.id));

      // Merge with currently selected businesses
      const mergedIds = new Set([...selectedBusinessIds, ...allBusinessIds]);
      setSelectedBusinessIds(mergedIds);

      // Call the update callback with all selected business IDs
      const businessArray = Array.from(mergedIds);
      await onUpdateBusinesses(businessArray);
    } catch (error) {
      // Revert on error
      if (initialSelectedBusinessIds) {
        setSelectedBusinessIds(new Set(initialSelectedBusinessIds));
      } else {
        setSelectedBusinessIds(previousSelectedIds);
      }
      console.error("Error selecting all businesses:", error);
    } finally {
      setIsLoadingAllBusinesses(false);
    }
  };

  // Use provided handleSelectAllBusinesses or internal implementation
  const handleSelectAllBusinessesFinal =
    handleSelectAllBusinesses || handleSelectAllBusinessesInternal;

  // Disable select buttons when there are no businesses to select
  const hasNoBusinesses = !isLoadingBusinesses && businesses.length === 0;
  const isSelectDisabled =
    assignBusinessMutation.isPending ||
    isLoadingBusinesses ||
    isLoadingAllBusinesses ||
    hasNoBusinesses;

  return (
    <div className="space-y-6">
      {/* Search and Controls */}
      <div className="border-border bg-card rounded-lg border p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Search */}
          <div className="min-w-0 flex-1">
            <div className="relative">
              <div className="ordering-brands-search-icon pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Icon
                  name="Search"
                  className="text-muted-foreground h-4 w-4 sm:h-5 sm:w-5"
                />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                aria-label={t("businessesTab.search.placeholder")}
                placeholder="Search businesses..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="ordering-brands-search-input border-border bg-background focus:ring-ring h-9 w-full rounded-lg border px-3 py-2 pr-8 pl-8 text-sm text-white placeholder-gray-400 focus:border-transparent focus:ring-2 focus:outline-none sm:h-10 sm:px-4 sm:pr-10 sm:pl-10"
              />
              {searchInput && (
                <Button
                  variant="ghost"
                  onClick={clearSearchAndRestoreFocus}
                  aria-label={t("stores:search.clearSearch")}
                  className="ordering-brands-search-clear bg-muted hover:bg-muted absolute top-1/2 right-2 flex h-4 w-4 -translate-y-1/2 transform cursor-pointer items-center justify-center rounded-full transition-colors sm:h-5 sm:w-5"
                  type="button"
                >
                  <svg
                    className="text-foreground h-2.5 w-2.5 hover:text-white sm:h-3 sm:w-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </Button>
              )}
            </div>
          </div>

          {/* Select All/None Buttons */}
          <div className="flex flex-wrap items-center space-x-2">
            <Button
              variant="ghost"
              type="button"
              onClick={handleSelectAll}
              disabled={isSelectDisabled}
              className="text-primary hover:text-primary flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              title={
                hasNoBusinesses
                  ? "No businesses available to select"
                  : "Select all businesses on this page"
              }
            >
              <Icon name="CheckSquare" className="h-4 w-4" />
              <span>Select All (Page)</span>
            </Button>
            {(handleSelectAllBusinesses || onUpdateBusinesses) && (
              <Button
                variant="ghost"
                type="button"
                onClick={handleSelectAllBusinessesFinal}
                disabled={isSelectDisabled}
                className="text-success hover:text-success flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  hasNoBusinesses
                    ? "No businesses available to select"
                    : searchTerm
                      ? `Select all businesses matching "${searchTerm}"`
                      : "Select all businesses"
                }
              >
                <Icon name="CheckSquare" className="h-4 w-4" />
                <span>Select All Businesses</span>
              </Button>
            )}
            {handleSelectNone && (
              <Button
                variant="ghost"
                type="button"
                onClick={handleSelectNone}
                disabled={isSelectDisabled}
                className="text-muted-foreground hover:text-foreground flex items-center space-x-1 px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  hasNoBusinesses
                    ? "No businesses available"
                    : "Deselect all businesses"
                }
              >
                <Icon name="Square" className="h-4 w-4" />
                <span>Select None</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Business List */}
      {isLoadingBusinesses ? (
        <div
          className="flex items-center justify-center py-12"
          role="status"
          aria-live="polite"
        >
          <div className="text-center">
            <div className="border-primary mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2"></div>
            <p className="text-muted-foreground">Loading businesses...</p>
          </div>
        </div>
      ) : businesses.length > 0 ? (
        <>
          <div className="space-y-3">
            {businesses.map((business) => (
              <div
                key={business.id}
                className="border-border hover:bg-card flex items-center justify-between rounded-lg border p-4 transition-colors"
              >
                <div className="flex flex-1 items-center space-x-3">
                  <div className="relative">
                    <input
                      type="checkbox"
                      aria-labelledby={`brand-business-${business.id}-name`}
                      checked={selectedBusinessIds.has(business.id)}
                      onChange={() => handleBusinessToggle(business.id)}
                      disabled={assignBusinessMutation.isPending}
                      className="border-border bg-card text-primary focus:ring-ring h-5 w-5 cursor-pointer rounded focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                  </div>

                  {business.logo ? (
                    <img
                      src={business.logo}
                      alt={business.name}
                      className="h-12 w-12 flex-shrink-0 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="bg-secondary flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg">
                      <Icon
                        name="Store"
                        className="text-muted-foreground h-6 w-6"
                      />
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <h3
                      id={`brand-business-${business.id}-name`}
                      className="text-sm leading-tight font-medium text-white"
                    >
                      {business.name}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="mt-6">
              <div className="border-border bg-card rounded-lg border p-4">
                <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="text-muted-foreground text-sm">
                    Showing{" "}
                    <span className="font-medium text-white">
                      {(currentPage - 1) * 10 + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-medium text-white">
                      {Math.min(currentPage * 10, pagination.total || 0)}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium text-white">
                      {pagination.total || 0}
                    </span>{" "}
                    businesses
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={pagination.total_pages || 1}
                    onPageChange={handlePageChange}
                    maxVisiblePages={5}
                    disabled={isLoadingBusinesses}
                    className="mt-4 sm:mt-0"
                  />
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="py-12 text-center">
          <Icon
            name="Store"
            className="text-muted-foreground mx-auto mb-4 h-12 w-12"
          />
          <h3 className="mb-2 text-lg font-medium text-white">
            No Businesses Found
          </h3>
          <p className="text-muted-foreground">
            {searchTerm
              ? "Try adjusting your search terms."
              : "No businesses available."}
          </p>
        </div>
      )}
    </div>
  );
};

export default BusinessesTab;
